<template>
  <div v-if="temLeitura" class="tw-w-full">
    <div :class="theme === 'dark' ? 'tw-text-slate-100' : 'tw-text-slate-900'" class="tw-rounded-xl tw-p-4 tw-mb-6">
      <h4 :class="theme === 'dark' ? 'tw-text-slate-200' : 'tw-text-slate-700'" class="xs:tw-text-xs sm:tw-text-sm tw-font-semibold tw-mb-3">Informações climáticas no momento da leitura:</h4>
      <div v-if="termometria.estacao" class="tw-flex tw-items-center tw-gap-4 xs:tw-text-xs sm:tw-text-sm">
        <span class="tw-font-mono tw-font-bold">{{ termometria.estacao.temperatura }} °C</span>
        <span class="tw-font-mono tw-font-bold">{{ termometria.estacao.umidade }} %</span>
        <span :class="theme === 'dark' ? 'tw-text-slate-400' : 'tw-text-slate-500'" class="tw-text-xs">{{ termometria.data }}</span>
      </div>
      <div v-else :class="theme === 'dark' ? 'tw-text-slate-400' : 'tw-text-slate-500'" class="tw-text-sm">
        Sem dados de estação disponível
      </div>
    </div>

    <div v-if="emAndamento && isTermocloud" class="tw-mb-3 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold" :class="theme === 'dark' ? 'tw-text-amber-300' : 'tw-text-amber-600'">
      <span class="tw-inline-block tw-w-2 tw-h-2 tw-rounded-full tw-bg-current tw-animate-pulse"></span>
      {{ cabosRecebidos.length > 0 ? 'Leitura em andamento, aguardando os cabos restantes...' : 'Leitura em andamento, aguardando os cabos...' }}
    </div>

    <div class="tw-flex">
      <div :class="[
        'tw-rounded-2xl tw-p-1 tw-pb-6 tw-px-2 tw-min-w-max tw-flex tw-flex-col tw-justify-between tw-h-full tw-self-end',
        hasScrollbar && 'tw-mb-[8px]'
      ]">
        <div class="tw-flex tw-flex-col tw-gap-1">
          <div 
            v-for="sensor in maxSensores" 
            :key="sensor"
            @mouseover="sensorHover = (maxSensores - sensor) + 1"
            @mouseleave="sensorHover = null"
            :class="[
              'tw-rounded-lg tw-px-2 tw-py-1 tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold tw-text-xs sm:tw-text-sm tw-cursor-pointer tw-transition-all',
              sensorHover === ((maxSensores - sensor) + 1) 
                ? 'tw-bg-emerald-400 tw-ring-2 tw-ring-emerald-400' 
                : 'tw-bg-[#33b35a] hover:tw-ring-2 hover:tw-ring-emerald-400'
            ]"
          >
            S{{ (maxSensores - sensor) + 1 }}
          </div>
        </div>
      </div>
      <div class="tw-overflow-x-auto custom-scrollbar" ref="scrollContainer">
        <div class="tw-flex tw-gap-1 tw-min-w-max tw-pb-4">
          <div class="tw-flex tw-gap-1">
            <div 
              v-for="(cabo, index) in cabosExibicao"
              :key="index"
              :class="theme === 'dark' ? 'tw-bg-slate-700' : 'tw-bg-[#e9eef6]'"
              class="tw-rounded-2xl tw-p-1 tw-py-2 tw-min-w-max tw-flex tw-flex-col tw-justify-between tw-h-full"
            >
              <p
                :class="[
                  theme === 'dark' ? 'tw-text-slate-200' : 'tw-text-slate-800',
                  cabo.pending && 'tw-opacity-50'
                ]"
                class="tw-font-bold tw-text-center tw-mb-4 tw-text-xs sm:tw-text-sm"
              >
                Cabo {{ cabo.cabo }}
              </p>
              <div class="tw-flex tw-flex-col tw-gap-1">
                <template v-for="(sensor, sIndex) in cabo.sensores" :key="sIndex">
                  <div
                    v-if="sensorTemp(cabo, sensor) !== null && sensorTemp(cabo, sensor) !== undefined"
                    @mouseover="sensorHover = sensorPos(cabo, sensor)"
                    @mouseleave="sensorHover = null"
                    :class="[
                      'tw-min-w-[44px] sm:tw-min-w-[60px] tw-rounded-lg tw-px-1 tw-py-1 tw-text-center tw-font-bold tw-text-xs sm:tw-text-sm tw-text-black tw-cursor-pointer tw-transition-all',
                      sensorHover === sensorPos(cabo, sensor)
                        ? ['tw-ring-2 tw-scale-110', theme === 'dark' ? 'tw-ring-white' : 'tw-ring-black']
                        : 'hover:tw-ring-2 hover:tw-ring-emerald-400'
                    ]"
                    :style="{ backgroundColor: getCorPorTemperatura(sensorTemp(cabo, sensor)) }"
                  >
                    <span v-if="sensorTemp(cabo, sensor) < 99">
                      {{ sensorTemp(cabo, sensor) }}<span class="tw-hidden sm:tw-inline"> °C</span>
                    </span>
                    <span v-else>OFF</span>
                  </div>
                  <div
                    v-else
                    :class="[
                      theme === 'dark' ? 'tw-bg-slate-600/40 tw-text-slate-500' : 'tw-bg-slate-200 tw-text-slate-400',
                      !leituraExpirada && 'tw-animate-pulse'
                    ]"
                    class="tw-min-w-[44px] sm:tw-min-w-[60px] tw-rounded-lg tw-px-1 tw-py-1 tw-text-center tw-font-bold tw-text-xs sm:tw-text-sm"
                  >
                    —
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else :class="theme === 'dark' ? 'tw-text-slate-400' : 'tw-text-slate-500'" class="tw-text-sm">
      Nenhuma leitura disponível    
    </div>
</template>

<script>
import temperaturaCores from "@/assets/colors.json";
const JANELA_EM_ANDAMENTO_MS = 60 * 60 * 1000; // 1 hora

export default {
    props: {
        termometria: {
            type: Object,
            default: () => {},
        },
        silo: {
            type: Object,
            default: () => ({}),
        },
        leituraEmAndamento: {
            type: Boolean,
            default: false
        },
        theme: {
            type: String,
            default: 'light'
        }
    },
    data() {
        return {
            sensorHover: null,
            hasScrollbar: false,
            now: Date.now(),
            clockInterval: null
        };
    },
    mounted() {
        this.checkScrollbar();
        if (this.$refs.scrollContainer) {
            const resizeObserver = new ResizeObserver(() => {
                this.checkScrollbar();
            });
            resizeObserver.observe(this.$refs.scrollContainer);
        }
        this.clockInterval = setInterval(() => {
            this.now = Date.now();
        }, 60000);
    },
    beforeUnmount() {
        if (this.clockInterval) {
            clearInterval(this.clockInterval);
        }
    },
    computed: {
        isTermocloud() {
            return String(this.$store.state.unidade.unidade?.tipo_produto || '').toLowerCase() === 'termocloud';
        },
        cabosConfigurados() {
            return Array.isArray(this.silo?.cabos) ? this.silo.cabos : [];
        },
        cabosRecebidos() {
            return Array.isArray(this.termometria?.dados) ? this.termometria.dados : [];
        },
        leituraCarregada() {
            const t = this.termometria || {};
            return !!(t.data || t.estacao || Array.isArray(t.dados));
        },
        temLeitura() {
            return this.leituraCarregada || this.leituraEmAndamento;
        },
        leituraExpirada() {
            const criada = this.parseDataLeitura(this.termometria?.data);
            if (!criada) {
                return false;
            }
            return (this.now - criada.getTime()) >= JANELA_EM_ANDAMENTO_MS;
        },
        emAndamento() {
            if (this.leituraExpirada) {
                return false;
            }
            return this.cabosPendentes > 0
                || (this.leituraEmAndamento && this.cabosRecebidos.length === 0);
        },
        cabosExibicao() {
            const recebidosPorCabo = new Map(
                this.cabosRecebidos.map(d => [Number(d.cabo), d])
            );

            const base = this.cabosConfigurados.length > 0
                ? this.cabosConfigurados
                : this.cabosRecebidos;

            return base
                .slice()
                .sort((a, b) => a.cabo - b.cabo)
                .map(c => {
                    const leitura = recebidosPorCabo.get(Number(c.cabo));
                    return {
                        cabo: c.cabo,
                        sensores: c.sensores,
                        temperaturas: leitura ? leitura.temperaturas : null,
                        pending: !leitura
                    };
                });
        },
        cabosPendentes() {
            return this.cabosExibicao.filter(c => c.pending).length;
        },
        maxSensores() {
            const base = this.cabosConfigurados.length > 0
                ? this.cabosConfigurados
                : this.cabosRecebidos;
            return base.length ? Math.max(...base.map(c => c.sensores)) : 0;
        }
    },
    methods: {
        checkScrollbar() {
            if (this.$refs.scrollContainer) {
                this.hasScrollbar = this.$refs.scrollContainer.scrollWidth > this.$refs.scrollContainer.clientWidth;
            }
        },
        parseDataLeitura(valor) {
            if (!valor || typeof valor !== 'string') {
                return null;
            }
            const [dataParte, horaParte = '00:00:00'] = valor.trim().split(' ');
            const dmy = (dataParte || '').split('/');
            if (dmy.length !== 3) {
                return null;
            }
            const [dia, mes, ano] = dmy.map(Number);
            const [hora = 0, minuto = 0, segundo = 0] = horaParte.split(':').map(Number);
            if (!dia || !mes || !ano) {
                return null;
            }
            const data = new Date(ano, mes - 1, dia, hora, minuto, segundo);
            return isNaN(data.getTime()) ? null : data;
        },
        sensorPos(cabo, sensor) {
            return (cabo.sensores - sensor) + 1;
        },
        sensorTemp(cabo, sensor) {
            if (!cabo.temperaturas) {
                return null;
            }
            return cabo.temperaturas[`s${this.sensorPos(cabo, sensor)}`];
        },
        getCorPorTemperatura(temp) {
            temp = Number(temp);

            const faixas = Object.keys(temperaturaCores)
                .map(Number)
                .sort((a, b) => a - b);            

            for (let i = 0; i <= faixas.length - 1; i++) {
                if (temp <= faixas[i]) {
                    return temperaturaCores[faixas[i]];
                }
            }

            return "#c31313";
        }
    }
};
</script>
<style scoped>
.custom-scrollbar::-webkit-scrollbar {
    height: 8px;
}
</style>